import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, isRef as _isRef, vModelSelect as _vModelSelect, withDirectives as _withDirectives, createTextVNode as _createTextVNode, vModelText as _vModelText, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-3" }
const _hoisted_2 = { class: "flex flex-wrap items-center gap-3" }
const _hoisted_3 = { class: "text-xs text-muted flex items-center gap-2" }
const _hoisted_4 = ["value"]
const _hoisted_5 = ["value"]
const _hoisted_6 = { class: "text-xs text-muted flex items-center gap-2" }
const _hoisted_7 = { class: "text-xs text-muted flex items-center gap-2" }
const _hoisted_8 = ["value"]
const _hoisted_9 = {
  key: 0,
  class: "text-xs text-muted flex items-center gap-2"
}
const _hoisted_10 = { class: "flex items-center gap-1" }
const _hoisted_11 = { class: "sr-only" }
const _hoisted_12 = ["onUpdate:modelValue"]
const _hoisted_13 = { class: "ml-auto flex gap-2" }
const _hoisted_14 = ["disabled"]
const _hoisted_15 = {
  key: 0,
  class: "text-sm text-red-500"
}
const _hoisted_16 = {
  key: 1,
  class: "text-sm text-muted"
}
const _hoisted_17 = {
  key: 2,
  class: "grid grid-cols-1 md:grid-cols-2 gap-3"
}
const _hoisted_18 = { class: "bg-accent p-2 overflow-hidden flex flex-col gap-2" }
const _hoisted_19 = { style: {
                width: '100%',
                background: '#ffffff',
                borderRadius: '16px',
                boxShadow: 'rgb(4, 4, 4) 0px 0px 0px 1px, rgba(255, 255, 255, 0.18) 0px 1px 0px inset, rgba(0, 0, 0, 0.6) 0px 0px 18px 1px',
                overflow: 'hidden',
                position: 'relative',
                zIndex: 1,
              } }
const _hoisted_20 = { style: {
                  padding: '40px',
                  paddingLeft: '0px',
                  display: 'flex',
                  fontWeight: 600,
                  fontFamily: 'JetBrains Mono, Monaco, monospace',
                  fontSize: '24px',
                  lineHeight: '32px',
                  paddingTop: '48px',
                  paddingBottom: '64px',
                  color: '#d0d0d0',
                  background: '#292d3e',
                } }
const _hoisted_21 = { style: {
                    width: '68px',
                    flexShrink: 0,
                    paddingRight: '32px',
                    color: '#94a3b8',
                    fontSize: '16px',
                    fontVariantNumeric: 'tabular-nums',
                    userSelect: 'none',
                    lineHeight: '32px',
                  } }
const _hoisted_22 = { style: {"margin-top":"1.25rem"} }
const _hoisted_23 = { style: { position: 'absolute', inset: '0', overflow: 'hidden', zIndex: 0 } }
const _hoisted_24 = {
  key: 0,
  class: "bg-accent p-2 overflow-hidden flex flex-col gap-2"
}
const _hoisted_25 = ["src", "alt"]

import { domToBlob } from 'modern-screenshot'
import { BACKGROUND_STYLES, CUSTOM_EMOJI_PREFIX, DEFAULT_BACKGROUND_STYLE_ID, DEFAULT_GRADIENT_ID, GRADIENT_PRESETS, customEmojiBackgroundId, customGradientSpec, getBackgroundStyle, getForegroundStyle, getImageAltText, parseCustomBackgroundId, parseGradient } from '#shared/cms/backgrounds'
import { getHumanRelativeDate } from '#shared/cms/date-formatting'
import { ref } from 'vue'

// The blob is held locally until publish time (unreferenced PDS blobs
// get GC'd, so we wait until the record write that anchors it).
export interface GeneratedImage {
  blob: Blob
  width: number
  height: number
  backgroundStyleId: string
}

const BLUESKY_EMBED_MAX_BYTES = 950_000

const CUSTOM_GRADIENT = 'custom'


export default /*@__PURE__*/_defineComponent({
  __name: 'AmaImageGenerator',
  props: {
    question: {},
    createdAt: {},
    initialPreviewUrl: {},
    initialBackgroundStyleId: {}
  },
  emits: ["generated", "cleared", "removed"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

function canvasToBlob (canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise(resolve => canvas.toBlob(resolve, type, quality))
}

async function compressUnderLimit (source: Blob, width: number, height: number, maxBytes: number): Promise<Blob> {
  const bitmap = await createImageBitmap(source)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Could not get a canvas context to compress the image.')
  ctx.drawImage(bitmap, 0, 0, width, height)
  bitmap.close?.()

  let smallest: Blob | null = null
  for (const type of ['image/webp', 'image/jpeg']) {
    for (let quality = 0.92; quality >= 0.5; quality -= 0.07) {
      const candidate = await canvasToBlob(canvas, type, quality)
      if (!candidate) continue
      if (!smallest || candidate.size < smallest.size) smallest = candidate
      if (candidate.size <= maxBytes) return candidate
    }
  }
  if (smallest && smallest.size <= maxBytes) return smallest
  throw new Error(`Could not compress the image under ${maxBytes} bytes for Bluesky. Shorten the question or pick a simpler background.`)
}

/** Sentinel for the gradient select, distinct from any preset id. */
const backgroundStyleId = ref(props.initialBackgroundStyleId ?? DEFAULT_BACKGROUND_STYLE_ID)
const backgroundStyle = computed(() => getBackgroundStyle(backgroundStyleId.value))

const isCustomEmoji = ref(backgroundStyleId.value.startsWith(CUSTOM_EMOJI_PREFIX))
const initialCustom = isCustomEmoji.value ? parseCustomBackgroundId(backgroundStyleId.value) : undefined
const initialGradient = parseGradient(initialCustom?.gradient)

const customEmoji = ref(initialCustom?.emojis || '🪿🦢🥚')
const gradientSelection = ref(initialGradient?.presetId ?? (initialGradient ? CUSTOM_GRADIENT : DEFAULT_GRADIENT_ID))
const gradientAngle = ref(initialGradient?.angle ?? 135)
const gradientColours = ref<string[]>(
  initialGradient && !initialGradient.presetId
    ? initialGradient.colours
    : ['#fef3c7', '#bae6fd', '#c7d2fe'],
)

const gradientSpec = computed(() => gradientSelection.value === CUSTOM_GRADIENT
  ? customGradientSpec(gradientAngle.value, gradientColours.value)
  : gradientSelection.value)

// The select and the custom inputs feed a single stored id: presets keep their
// own id, custom backgrounds serialise into `emoji:…|…` so no extra field is
// needed.
const backgroundSelection = computed({
  get: () => isCustomEmoji.value ? CUSTOM_EMOJI_PREFIX : backgroundStyleId.value,
  set: (value: string) => {
    isCustomEmoji.value = value === CUSTOM_EMOJI_PREFIX
    backgroundStyleId.value = isCustomEmoji.value
      ? customEmojiBackgroundId(customEmoji.value, gradientSpec.value)
      : value
  },
})

watch([customEmoji, gradientSpec], ([emoji, gradient]) => {
  if (!isCustomEmoji.value) return
  backgroundStyleId.value = customEmojiBackgroundId(emoji, gradient)
})
const previewAltText = computed(() => getImageAltText(props.question, backgroundStyleId.value))
const foregroundStyle = computed(() => getForegroundStyle(backgroundStyle.value))

const relativeDate = computed(() => getHumanRelativeDate(props.createdAt))
const formattedText = computed(() => props.question.trim())
const hasContent = computed(() => Boolean(formattedText.value))

const generating = ref(false)
const error = ref<string | null>(null)

const terminalRef = ref<HTMLDivElement | null>(null)
const contentRef = ref<HTMLDivElement | null>(null)
const previewWrapperRef = ref<HTMLDivElement | null>(null)

// Scale the 1200px-wide terminal down to fit the wrapper. Capped at 1 so
// it doesn't up-scale on wide screens. `clientHeight` returns the
// pre-transform value, so we read it before applying the scale.
const previewScale = ref(0.4)
const previewWrapperHeight = ref<string>('auto')

function recalculatePreviewSize () {
  if (!previewWrapperRef.value || !terminalRef.value) return
  const wrapperWidth = previewWrapperRef.value.clientWidth || 480
  const scale = Math.min(1, wrapperWidth / 1200)
  previewScale.value = scale
  previewWrapperHeight.value = `${Math.ceil(terminalRef.value.clientHeight * scale)}px`
}

onMounted(() => {
  recalculatePreviewSize()
  if (typeof ResizeObserver !== 'undefined' && previewWrapperRef.value) {
    const ro = new ResizeObserver(() => recalculatePreviewSize())
    ro.observe(previewWrapperRef.value)
    onBeforeUnmount(() => ro.disconnect())
  }
})
const visualLineCount = ref(1)
watch([formattedText, backgroundStyleId, visualLineCount], () => nextTick(recalculatePreviewSize))

// Approximate the wrapped-line count by measuring rendered text into a
// hidden probe, so the line-number gutter sizes correctly.
function recalculateLineCount () {
  if (!formattedText.value) {
    visualLineCount.value = 1
    return
  }
  if (!contentRef.value) return
  const contentWidth = (contentRef.value.clientWidth || 960) - 40
  const probe = document.createElement('div')
  probe.style.cssText = `
    position: absolute;
    visibility: hidden;
    width: ${contentWidth}px;
    font-family: JetBrains Mono, Monaco, monospace;
    font-size: 24px;
    line-height: 32px;
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
    padding: 0;
    margin: 0;
  `
  probe.textContent = formattedText.value
  document.body.appendChild(probe)
  visualLineCount.value = Math.max(1, Math.ceil(probe.offsetHeight / 32))
  document.body.removeChild(probe)
}
watch([formattedText, backgroundStyleId], () => nextTick(recalculateLineCount), { immediate: true })
onMounted(recalculateLineCount)

const lineNumbers = computed(() => Array.from({ length: visualLineCount.value }, (_, i) => (i + 1).toString().padStart(2, ' ')))

// Object URL of the most recently generated blob (or the existing image
// passed in for an answered AMA). Cleared when the question or
// background changes so the user is prompted to regenerate.
const generatedUrl = ref<string | null>(props.initialPreviewUrl ?? null)
const lastSignature = ref<string | null>(props.initialPreviewUrl ? `${props.question}::${backgroundStyleId.value}` : null)

onBeforeUnmount(() => {
  if (generatedUrl.value?.startsWith('blob:')) URL.revokeObjectURL(generatedUrl.value)
})

watch([formattedText, backgroundStyleId], ([question, styleId]) => {
  const sig = `${question}::${styleId}`
  if (lastSignature.value && lastSignature.value !== sig) {
    if (generatedUrl.value?.startsWith('blob:')) URL.revokeObjectURL(generatedUrl.value)
    generatedUrl.value = null
    lastSignature.value = null
    emit('cleared')
  }
})

async function generate () {
  if (!hasContent.value || !terminalRef.value || generating.value) return
  generating.value = true
  error.value = null
  try {
    await nextTick()

    const node = terminalRef.value
    // Bluesky's `app.bsky.embed.images` rejects images larger than 2000px
    // on either axis; cap the capture scale (otherwise 2× retina).
    const BLUESKY_MAX = 2000
    const naturalWidth = node.clientWidth
    const naturalHeight = node.clientHeight
    const captureScale = Math.min(2, BLUESKY_MAX / naturalWidth, BLUESKY_MAX / naturalHeight)

    const blob = await domToBlob(node, {
      width: naturalWidth,
      height: naturalHeight,
      scale: captureScale,
      style: { transform: 'scale(1)', transformOrigin: 'top left' },
    })
    if (!blob) throw new Error('Image generation produced no blob.')

    const width = Math.round(naturalWidth * captureScale)
    const height = Math.round(naturalHeight * captureScale)

    const finalBlob = blob.size > BLUESKY_EMBED_MAX_BYTES
      ? await compressUnderLimit(blob, width, height, BLUESKY_EMBED_MAX_BYTES)
      : blob

    if (generatedUrl.value?.startsWith('blob:')) URL.revokeObjectURL(generatedUrl.value)
    generatedUrl.value = URL.createObjectURL(finalBlob)
    lastSignature.value = `${formattedText.value}::${backgroundStyleId.value}`

    emit('generated', {
      blob: finalBlob,
      width,
      height,
      backgroundStyleId: backgroundStyleId.value,
    })
  }
  catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
  finally {
    generating.value = false
  }
}

function clear () {
  if (generatedUrl.value?.startsWith('blob:')) URL.revokeObjectURL(generatedUrl.value)
  generatedUrl.value = null
  lastSignature.value = null
  emit('removed')
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _cache[10] || (_cache[10] = _createElementVNode("h3", { class: "text-sm text-muted" }, " Generated image ", -1)),
      _createElementVNode("label", _hoisted_3, [
        _cache[6] || (_cache[6] = _createTextVNode(" Background ", -1)),
        _withDirectives(_createElementVNode("select", {
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(backgroundSelection) ? (backgroundSelection).value = $event : null)),
          class: "bg-accent px-2 py-1"
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(BACKGROUND_STYLES), (bg) => {
            return (_openBlock(), _createElementBlock("option", {
              key: bg.id,
              value: bg.id
            }, _toDisplayString(bg.title), 9, _hoisted_4))
          }), 128)),
          _createElementVNode("option", { value: _unref(CUSTOM_EMOJI_PREFIX) }, " Custom emoji… ", 8, _hoisted_5)
        ], 512), [
          [_vModelSelect, _unref(backgroundSelection)]
        ])
      ]),
      (isCustomEmoji.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("label", _hoisted_6, [
              _cache[7] || (_cache[7] = _createTextVNode(" Emoji ", -1)),
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((customEmoji).value = $event)),
                type: "text",
                class: "bg-accent px-2 py-1 w-32",
                placeholder: "🪿🦢🥚"
              }, null, 512), [
                [_vModelText, customEmoji.value]
              ])
            ]),
            _createElementVNode("label", _hoisted_7, [
              _cache[8] || (_cache[8] = _createTextVNode(" Gradient ", -1)),
              _withDirectives(_createElementVNode("select", {
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((gradientSelection).value = $event)),
                class: "bg-accent px-2 py-1"
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(GRADIENT_PRESETS), (gradient) => {
                  return (_openBlock(), _createElementBlock("option", {
                    key: gradient.id,
                    value: gradient.id
                  }, _toDisplayString(gradient.title), 9, _hoisted_8))
                }), 128)),
                _createElementVNode("option", { value: CUSTOM_GRADIENT }, " Custom… ")
              ], 512), [
                [_vModelSelect, gradientSelection.value]
              ])
            ]),
            (gradientSelection.value === CUSTOM_GRADIENT)
              ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                  _createElementVNode("label", _hoisted_10, [
                    _cache[9] || (_cache[9] = _createTextVNode(" Angle ", -1)),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((gradientAngle).value = $event)),
                      type: "number",
                      min: "0",
                      max: "359",
                      class: "bg-accent px-2 py-1 w-16"
                    }, null, 512), [
                      [
                        _vModelText,
                        gradientAngle.value,
                        void 0,
                        { number: true }
                      ]
                    ])
                  ]),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(gradientColours.value, (_, index) => {
                    return (_openBlock(), _createElementBlock("label", {
                      key: index,
                      class: "flex items-center gap-1"
                    }, [
                      _createElementVNode("span", _hoisted_11, "Colour " + _toDisplayString(index + 1), 1),
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": ($event: any) => ((gradientColours.value[index]) = $event),
                        type: "color",
                        class: "bg-accent h-7 w-8"
                      }, null, 8, _hoisted_12), [
                        [_vModelText, gradientColours.value[index]]
                      ])
                    ]))
                  }), 128)),
                  (gradientColours.value.length > 2)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        type: "button",
                        class: "hover:text-red-500",
                        onClick: _cache[4] || (_cache[4] = ($event: any) => (gradientColours.value.pop()))
                      }, " − "))
                    : _createCommentVNode("", true),
                  (gradientColours.value.length < 4)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 1,
                        type: "button",
                        class: "hover:text-primary",
                        onClick: _cache[5] || (_cache[5] = ($event: any) => (gradientColours.value.push('#c7d2fe')))
                      }, " + "))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true)
          ], 64))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_13, [
        _createElementVNode("button", {
          type: "button",
          disabled: !_unref(hasContent) || generating.value,
          class: "bg-primary text-background px-3 py-1 text-sm hover:bg-primary/90 transition-colors disabled:opacity-60",
          onClick: generate
        }, _toDisplayString(generating.value ? 'Generating…' : (generatedUrl.value ? 'Regenerate' : 'Generate')), 9, _hoisted_14),
        (generatedUrl.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              type: "button",
              class: "text-sm text-muted hover:text-red-500",
              onClick: clear
            }, " Remove "))
          : _createCommentVNode("", true)
      ])
    ]),
    (error.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_15, _toDisplayString(error.value), 1))
      : _createCommentVNode("", true),
    (!_unref(hasContent))
      ? (_openBlock(), _createElementBlock("p", _hoisted_16, " Once the question text is set, click Generate to render the image. It stays in your browser until you publish. "))
      : _createCommentVNode("", true),
    (_unref(hasContent))
      ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
          _createElementVNode("figure", _hoisted_18, [
            _cache[13] || (_cache[13] = _createElementVNode("figcaption", { class: "text-xs text-muted" }, " Source preview ", -1)),
            _createElementVNode("div", {
              ref_key: "previewWrapperRef",
              ref: previewWrapperRef,
              class: "relative overflow-hidden",
              style: _normalizeStyle({ height: previewWrapperHeight.value })
            }, [
              _createElementVNode("div", {
                ref_key: "terminalRef",
                ref: terminalRef,
                style: _normalizeStyle({
              width: '1200px',
              height: 'auto',
              padding: '96px',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              position: 'relative',
              transform: `scale(${previewScale.value})`,
              transformOrigin: 'top left',
            })
              }, [
                _createElementVNode("div", _hoisted_19, [
                  _cache[11] || (_cache[11] = _createStaticVNode("<div style=\"height:64px;background:#292d3e;border-top-left-radius:16px;border-top-right-radius:16px;display:flex;align-items:center;padding-left:20px;position:relative;\"><div style=\"display:flex;gap:14px;\"><div style=\"width:20px;height:20px;border-radius:50%;background:#ff5f57;border:1px solid #e53e3e;\"></div><div style=\"width:20px;height:20px;border-radius:50%;background:#ffbd2e;border:1px solid #d69e2e;\"></div><div style=\"width:20px;height:20px;border-radius:50%;background:#28ca42;border:1px solid #38a169;\"></div></div><div style=\"position:absolute;left:50%;transform:translateX(-50%);color:#64748b;font-size:24px;white-space:nowrap;font-weight:500;font-family:-apple-system, BlinkMacSystemFont, &#39;Segoe UI&#39;, Roboto, sans-serif;\"> ~/question.md </div></div>", 1)),
                  _createElementVNode("div", _hoisted_20, [
                    _createElementVNode("div", _hoisted_21, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(lineNumbers), (n) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: n,
                          style: {"height":"32px","display":"flex","justify-content":"flex-end"}
                        }, _toDisplayString(n), 1))
                      }), 128))
                    ]),
                    _createElementVNode("div", {
                      ref_key: "contentRef",
                      ref: contentRef,
                      style: {
                    flex: 1,
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    lineHeight: '32px',
                    paddingRight: '40px',
                    maxWidth: 'calc(100% - 68px)',
                    overflowWrap: 'break-word',
                  }
                    }, _toDisplayString(_unref(formattedText)), 513)
                  ])
                ]),
                _createElementVNode("div", {
                  style: _normalizeStyle({
                ..._unref(foregroundStyle),
                fontFamily: '-apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, sans-serif',
                fontSize: '24px',
                marginRight: '1rem',
                flexDirection: 'row',
                justifyContent: 'space-between',
                display: 'flex',
                whiteSpace: 'nowrap',
                textAlign: 'right',
                fontWeight: '500',
                position: 'relative',
                zIndex: 1,
              })
                }, [
                  _cache[12] || (_cache[12] = _createElementVNode("div", { style: {"margin-top":"1.25rem"} }, " roe.dev/ama ", -1)),
                  _createElementVNode("div", _hoisted_22, " asked " + _toDisplayString(_unref(relativeDate)), 1)
                ], 4),
                _createElementVNode("div", _hoisted_23, [
                  _createElementVNode("div", {
                    style: _normalizeStyle({ position: 'absolute', inset: '-9em', ..._unref(backgroundStyle).style })
                  }, null, 4)
                ])
              ], 4)
            ], 4)
          ]),
          (generatedUrl.value)
            ? (_openBlock(), _createElementBlock("figure", _hoisted_24, [
                _cache[14] || (_cache[14] = _createElementVNode("figcaption", { class: "text-xs text-muted" }, " Generated image ", -1)),
                _createElementVNode("img", {
                  src: generatedUrl.value,
                  alt: _unref(previewAltText),
                  class: "block w-full h-auto"
                }, null, 8, _hoisted_25)
              ]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})